import {ModalOverlay, ModalXCloseButton} from '@/components/ui/modal-atoms/ModalAtoms.tsx'
import {
    StyledConversationsModal,
    StyledConversationsModalBody,
    StyledConversationsModalBodyContainer,
    StyledConversationsModalBodyOverlay,
    StyledConversationsModalHeader,
    StyledConversationsModalSubheader,
    StyledConversationsModalFooter,
    StyledBorderlessComboboxInput
} from '@/features/conversations/components/conversations-modal/style.ts'
import {Flexbox} from '@/components/ui/flexbox/FlexBox.tsx'
import {memo, useState, useEffect, useId} from 'react'
import {GroupMember} from '@/features/users/types'
import {Button} from '@/components/ui/button/Button.tsx'
import {PlusIcon, ArrowLeftIcon, SearchSmIcon, ChevronDownIcon} from '@/components/ui/icon/Icon'
import {ConversationsList} from '@/features/conversations/components/conversations-list/ConversationsList'
import {ConversationMessages} from '@/features/conversations/components/conversation-messages/ConversationMessages'
import {Conversation} from '@/features/conversations/types'
import {useTranslation} from 'react-i18next'
import {ComboboxProvider, useComboboxStore} from '@ariakit/react'
import {useCreateConversation} from '@/features/conversations/query/useCreateConversation'
import type {CreateConversationPayload} from '@/features/conversations/http/conversations.http'
import {NewConversationForm} from '../new-conversation-form/NewConversationForm'
import {ConversationParticipants} from '../conversation-participants/ConversationParticipants'
import {useInputSearch} from '@/hooks/useInputSearch'
import {Text} from '@/components/ui/text/Text'
import {errorHandler} from '@/utilities/helpers'
import {useConversations} from '@/features/conversations/query/useConversations'
import {usePermissions} from '@/features/auth/store/store'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {NotificationContextBar} from '@/features/notifications/components/notification-context-bar/NotificationContextBar'
import {useNotificationContextHostStore} from '@/features/notifications/store/useNotificationContextHostStore'
import {useNotificationWorkContext} from '@/features/notifications/hooks/useNotificationWorkContext'

interface ConversationsModalProps {
    isOpen: boolean
    onClose: () => void
    /** Patient scope for the conversations, or null for internal conversations (no patient) */
    patientId: string | null
    initialConversationId?: string | null
    onClearInitialConversation?: () => void
    /**
     * Lets this panel host the notification work-context bar in place of its own header.
     *
     * `searchParam` is the deep link this panel is opened by — the bar clears it in the same
     * navigation as the work context. `closeSurface` closes the panel's UI state *without*
     * touching the address, which is what keeps that a single navigation.
     */
    notificationContextHost?: {searchParam?: string; closeSurface: () => void}
}

const ConversationsModalComponent = ({
    isOpen,
    onClose,
    patientId,
    initialConversationId,
    onClearInitialConversation,
    notificationContextHost
}: ConversationsModalProps) => {
    const {t} = useTranslation()
    const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
    const [isCreatingNewConversation, setIsCreatingNewConversation] = useState(false)
    const [selectedMembers, setSelectedMembers] = useState<GroupMember[]>([])
    const {searchValue, onSearch} = useInputSearch()
    const permissions = usePermissions()
    const comboboxStore = useComboboxStore({
        setValue: onSearch
    })

    const {conversations} = useConversations(patientId, 30)

    // While this panel is open it hosts the work-context bar itself instead of being covered
    // by the wide band. The claim is made because *this surface* is open — never because of
    // the notification's type — so any notification landing here is hosted the same way.
    const hostId = useId()
    const claimNotificationContextHost = useNotificationContextHostStore(state => state.claim)
    const releaseNotificationContextHost = useNotificationContextHostStore(state => state.release)
    const {phase: notificationContextPhase} = useNotificationWorkContext()
    const hasNotificationContext = notificationContextPhase !== 'idle'

    const hostSearchParam = notificationContextHost?.searchParam
    useEffect(() => {
        if (!isOpen) return
        claimNotificationContextHost(hostId, hostSearchParam ? [hostSearchParam] : [])
        return () => releaseNotificationContextHost(hostId)
    }, [isOpen, hostId, hostSearchParam, claimNotificationContextHost, releaseNotificationContextHost])

    useEffect(() => {
        // Also switches to a different conversation when initialConversationId changes
        // while the modal is already open (e.g. clicking another conversation notification)
        if (
            initialConversationId &&
            conversations.length > 0 &&
            isOpen &&
            selectedConversation?.id !== initialConversationId
        ) {
            const targetConversation = conversations.find(conv => conv.id === initialConversationId)
            if (targetConversation) {
                setSelectedConversation(targetConversation)
            }
        }
    }, [initialConversationId, conversations, isOpen, selectedConversation])

    useEffect(() => {
        if (!isOpen) {
            setSelectedConversation(null)
            setIsCreatingNewConversation(false)
            setSelectedMembers([])
        }
    }, [isOpen])

    const createConversationMutation = useCreateConversation({
        onSuccess: newConversation => {
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.CONVERSATIONS, patientId]})
            setIsCreatingNewConversation(false)
            setSelectedConversation(newConversation)
        },
        onError: errorHandler
    })

    const handleCreateNewConversation = () => {
        setSelectedMembers([])
        setIsCreatingNewConversation(true)
    }

    const handleCancelNewConversation = () => {
        setIsCreatingNewConversation(false)
        setSelectedMembers([])
    }

    const handleSubmitNewConversation = (
        data: {
            title: string
            message: string
            memberIds: string[]
            isUrgent: boolean
            entityId?: string
        },
        members: {type: 'user' | 'group'; id: string}[]
    ) => {
        const payload: CreateConversationPayload = {
            // Internal conversations (patientId null) are created without a patient
            ...(patientId !== null && {patientId}),
            title: data.title,
            message: data.message,
            isUrgent: data.isUrgent,
            members: members,
            entityId: data.entityId
        }

        createConversationMutation.mutate(payload)
    }

    const handleSelectConversation = (conversation: Conversation) => {
        setSelectedConversation(conversation)
    }

    const handleDeleteConversation = (conversationId: string) => {
        if (selectedConversation?.id === conversationId) {
            setSelectedConversation(null)
        }
    }

    const handleBackToList = () => {
        setSelectedConversation(null)
        setIsCreatingNewConversation(false)
        setSelectedMembers([])
        onSearch('')

        if (onClearInitialConversation) {
            onClearInitialConversation()
        }
    }

    if (!isOpen) return null

    const title = selectedConversation
        ? selectedConversation.title
        : patientId === null
          ? t('conversations:internal_title')
          : t('conversations:title')

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <StyledConversationsModal width="500px" onClick={event => event.stopPropagation()}>
                <Flexbox direction="column" fullWidth>
                    {hasNotificationContext ? (
                        <NotificationContextBar
                            variant="hosted"
                            trailingSlot={<ModalXCloseButton onClick={onClose} />}
                            onCloseHost={notificationContextHost?.closeSurface}
                        />
                    ) : (
                        <StyledConversationsModalHeader direction="column" gap={4} fullWidth>
                            <Flexbox justify="space-between" align="center" fullWidth>
                                <Flexbox align="center" gap={2}>
                                    {selectedConversation && (
                                        <Button variant="ghost" shape="square" onClick={handleBackToList}>
                                            <ArrowLeftIcon />
                                        </Button>
                                    )}
                                    <Text size="textXl" palette="neutral" shade="900" weight="500">
                                        {title}
                                    </Text>
                                </Flexbox>
                                <ModalXCloseButton onClick={onClose} />
                            </Flexbox>
                        </StyledConversationsModalHeader>
                    )}
                    <StyledConversationsModalSubheader align="center" direction="row" fullWidth>
                        {!selectedConversation ? (
                            <Flexbox direction="column">
                                <ComboboxProvider store={comboboxStore} resetValueOnHide>
                                    <Flexbox fullWidth>
                                        <StyledBorderlessComboboxInput
                                            typeIcon={<SearchSmIcon size={16} />}
                                            placeholder={t('conversations:search_placeholder')}
                                            inputSize="sm"
                                            disabled={isCreatingNewConversation}
                                        />
                                    </Flexbox>
                                </ComboboxProvider>
                            </Flexbox>
                        ) : (
                            <ConversationParticipants
                                conversationId={selectedConversation.id}
                                participants={selectedConversation.members}
                                patientId={patientId}
                            />
                        )}
                    </StyledConversationsModalSubheader>
                </Flexbox>

                <StyledConversationsModalBodyContainer>
                    <StyledConversationsModalBody>
                        {selectedConversation ? (
                            <ConversationMessages
                                conversationId={selectedConversation.id}
                                hasUnreadMessages={selectedConversation.hasUnreadMessages}
                                patientId={patientId}
                            />
                        ) : (
                            <ConversationsList
                                patientId={patientId}
                                onSelectConversation={handleSelectConversation}
                                selectedConversationId={undefined}
                                search={searchValue}
                                onDeleteConversation={handleDeleteConversation}
                            />
                        )}
                    </StyledConversationsModalBody>
                    {isCreatingNewConversation && !selectedConversation && <StyledConversationsModalBodyOverlay />}
                </StyledConversationsModalBodyContainer>

                {!selectedConversation && permissions?.conversations?.write && (
                    <StyledConversationsModalFooter justify="center">
                        {isCreatingNewConversation ? (
                            <Flexbox direction="column" fullWidth gap={2} align="end">
                                <Button size="xs" variant="ghost" onClick={() => setIsCreatingNewConversation(false)}>
                                    {t('commons:close')}
                                    <ChevronDownIcon />
                                </Button>
                                <NewConversationForm
                                    patientId={patientId}
                                    selectedMembers={selectedMembers}
                                    onMembersChange={setSelectedMembers}
                                    onCancel={handleCancelNewConversation}
                                    onSubmit={handleSubmitNewConversation}
                                    isPendingCreatingNewConversation={createConversationMutation.isPending}
                                />
                            </Flexbox>
                        ) : (
                            <Button fullWidth variant="secondary" onClick={handleCreateNewConversation}>
                                <PlusIcon />
                                {t('conversations:new_cta')}
                            </Button>
                        )}
                    </StyledConversationsModalFooter>
                )}
            </StyledConversationsModal>
        </>
    )
}

export const ConversationsModal = memo(ConversationsModalComponent)
